import React from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// MUI
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Unstable_Grid2'; // TODO:  probably need to update this import in the future
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs';
import MenuItem from '@mui/material/MenuItem';
// components
import { 
  FormProvider, 
  RHFTextField, 
  RHFDatepicker,
  RHFSelect
} from '../../hook-form';
import { useDispatch } from '../../../redux/store';
import { setUpdateEcommStoreFields } from '../../../redux/thunks/ecommStoresThunks';

// ----------------------------------------------------------------------

PricingCard.propTypes = {
  storeData: PropTypes.object.isRequired,
};


const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : theme.palette.primary.lighter,
  ...theme.typography.body2,
  padding: theme.spacing(1.5),
  textAlign: 'center',
  color: theme.palette.text.secondary,

}));

export default function PricingCard({ storeData }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // console.log("storeData", storeData)

  const defaultValues = {
    store_title: storeData.store_title ?? '',
    store_id: storeData.store_id ?? '',
    platform: storeData.platform ?? '',
    recurring_application_charge_id: storeData?.shopify_billing?.recurring_application_charge_id ?? '',

    // Pricing
    model_limit: Number(storeData.model_limit) || 0,
    fixed_price: storeData?.shopify_billing?.fixed_price !== undefined ? Number(storeData.shopify_billing.fixed_price) : 0,
    capped_amount: Number(storeData?.shopify_billing?.capped_amount) || 0,
    billing_on: dayjs(storeData?.shopify_billing?.billing_on ?? dayjs()),
    status:
      storeData.platform === 'shopify'
        ? storeData?.shopify_billing?.status ?? 'pending'
        : storeData?.stripe_billing?.status ?? 'pending',
  };

  const PricingSchema = Yup.object().shape({
    store_title: Yup.string().nullable(),
    store_id: Yup.string().nullable(),
    platform: Yup.string().nullable(),
    recurring_application_charge_id: Yup.string().nullable(),

    // Pricing
    model_limit: Yup.number().nullable(),
    fixed_price: Yup.number().nullable(),
    capped_amount: Yup.number().nullable(),
    billing_on: Yup.string().nullable(),
    status: Yup.string().nullable(),
  });

  const methods = useForm({
    resolver: yupResolver(PricingSchema),
    defaultValues,
  });

  const {
    setValue,
    watch,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();


  // Submit edited options
  const onSubmit = async () => {
    try {
      const payload = {};
      const fields = ['fixed_price', 'model_limit', 'capped_amount', 'billing_on'];
      
      // Only include status field for Shopify stores
      if (values.platform === 'shopify') {
        fields.push('status');
      }
      
      fields.forEach(field => {
        if (field === 'billing_on') {
          // Compare dayjs objects by their ISO string values
          const currentISO = values.billing_on?.toISOString();
          const defaultISO = defaultValues.billing_on?.toISOString();
          if (currentISO !== defaultISO) {
            payload[field] = values.billing_on;
          }
        } else if (values[field] !== defaultValues[field]) {
          // Ensure numeric fields are properly converted to numbers
          if (['fixed_price', 'model_limit', 'capped_amount'].includes(field)) {
            payload[field] = Number(values[field]);
          } else {
            payload[field] = values[field];
          }
        }
      });

      // Map status to correct API field
      if ('status' in payload) {
        if (values.platform === 'shopify') {
          payload.shopify_billing_status = payload.status;
        } else {
          payload.stripe_billing_status = payload.status;
        }
        delete payload.status;
      }

      if(Object.keys(payload).length > 0) {
        await dispatch(setUpdateEcommStoreFields({ fieldsToUpdate: payload, storeID: values.store_id, platform: values.platform }));
        enqueueSnackbar("Pricing settings updated successfully!", { variant: 'success' });
      } else {
        enqueueSnackbar("No changes detected. Please modify at least one field before saving.", { variant: 'info' });
      }
    } catch (error) {
      console.error("onSubmit error", error);
    }
  };

  return (
    <Card sx={{ p: 2 }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="column">
          <Box sx={{ px: 1 }}>
            <Typography variant="h6" align="center" color="custom.light" gutterBottom sx={{ whiteSpace: 'pre-line' }}>
              Pricing
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Item>
                <RHFTextField 
                  name="fixed_price" 
                  size="small" 
                  label="Fixed Price" 
                  type="number"
                  inputProps={{ min: 0 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item>
                <RHFTextField name="model_limit" size="small" label="Model Limit" type="number" />
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item>
                <RHFTextField name="capped_amount" size="small" label="Capped Amount" type="number" />
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <RHFDatepicker name="billing_on" label="Billing On" size="small" />
                </LocalizationProvider>
              </Item>
            </Grid>
            
              <Grid item xs={6}>
                <Item>
                  <RHFSelect 
                    name="status" 
                    label="Status" 
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </RHFSelect>
                </Item>
              </Grid>
            
          </Grid>

          <Box sx={{ mt: 1, p: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Button
                variant="outlined" 
                size="medium" 
                onClick={() => reset()}
              >
                Reset
              </Button>
              <LoadingButton 
                variant="contained" 
                size="medium" 
                type="submit"
                loading={isSubmitting}
              >
                Save
              </LoadingButton>
            </Stack>
          </Box>
        </Stack>
      </FormProvider>
    </Card>
  )
};